<template>
  <div class="pb-20 pt-36">
    <!-- SEO Meta Tags -->
    <PageSEO
      title="Vedmant - Lead Developer & Founder"
      description="Meet Vedmant, our lead developer and founder with 20+ years of experience in full-stack web development. Expert in modern technologies and digital solutions."
      :keywords="['Vedmant', 'lead developer', 'founder', 'full-stack developer', 'web development expert', '20+ years experience']"
      url="https://vedware.com/about/vedmant"
    />

    <div class="container mx-auto">
      <!-- Page Header -->
      <PageHeader
        title="Meet Our Lead Developer"
        description="Full-stack developer with 20+ years of experience"
      />

      <!-- Developer Profile -->
      <div class="mt-10 block sm:mt-20 sm:flex sm:gap-10">
        <!-- About profile image and social links -->
        <div class="mb-7 w-full sm:mb-0 sm:w-1/4">
          <img src="/images/arthur.jpg" class="w-96 rounded-lg shadow-soft" alt="Developer Profile">

          <!-- Social Links -->
          <SocialLinks :social-links="socialLinks" />
        </div>

        <!-- About details -->
        <div class="w-full text-left text-lg text-ternary-dark dark:text-ternary-light sm:w-3/4">
          <!-- Introduction -->
          <div class="mb-8">
            <h2 class="mb-4 text-3xl font-bold text-ternary-dark dark:text-primary-light">
              About Arthur
            </h2>
            <p class="mb-4 leading-relaxed">
              He brings over <span class="font-semibold text-accent dark:text-accent">13 years of freelance experience</span> and nearly <span class="font-semibold text-accent dark:text-accent">20 years in the tech world</span>. Specializing in building custom web applications that are clean, scalable, and easy to maintain - from backend architecture to responsive, user-friendly interfaces.
            </p>
            <p class="mb-4 leading-relaxed">
              He has successfully delivered a wide range of projects including <span class="font-semibold">SaaS platforms</span>, <span class="font-semibold">e-commerce systems</span>, <span class="font-semibold">media-rich websites</span>, and <span class="font-semibold">travel search platforms</span>, often working as a solo developer or in small, flexible remote teams.
            </p>
          </div>

          <!-- Frontend Expertise -->
          <ExpertiseSection
            title="Frontend Excellence"
            emoji="🎨"
            description="He specializes in building modern, responsive frontends with <span class='font-semibold text-accent dark:text-accent'>Vue.js</span>, <span class='font-semibold text-accent dark:text-accent'>Nuxt</span>, and <span class='font-semibold text-accent dark:text-accent'>Tailwind CSS</span>. His goal is to keep interfaces clean, fast, and intuitive, while maintaining solid integration with backend services."
            :cards="frontendCards"
          />

          <!-- Backend Expertise -->
          <ExpertiseSection
            title="Backend Architecture"
            emoji="🔧"
            description="On the backend, he primarily works with <span class='font-semibold text-accent dark:text-accent'>Laravel</span> and <span class='font-semibold text-accent dark:text-accent'>Node.js</span>, designing APIs, handling authentication, and integrating various services. He writes clean, testable code with a strong focus on performance and maintainability."
            :cards="backendCards"
          />

          <!-- Work Style & Strengths -->
          <InfoCards :cards="infoCards" />

          <!-- Call to Action -->
          <div class="mt-8 rounded-xl bg-gradient-to-r from-accent/10 to-gradient-end/10 p-6 text-center dark:from-accent/20 dark:to-gradient-end/20">
            <p class="mb-4 text-lg font-medium text-ternary-dark dark:text-primary-light">
              Looking for a developer who can own both sides of your stack and drive your project forward?
            </p>
            <p class="font-semibold text-accent dark:text-accent">
              Let's talk about your next project.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Statistics -->
    <AboutCounter />

    <!-- Brands Section -->
    <AboutClients />
  </div>
</template>

<script setup lang="ts">
import { ref } from '#imports'
import AboutClients from '~/components/About/AboutClients.vue'
import AboutCounter from '~/components/About/AboutCounter.vue'
import ExpertiseSection from '~/components/About/ExpertiseSection.vue'
import InfoCards from '~/components/About/InfoCards.vue'
import PageHeader from '~/components/About/PageHeader.vue'
import SocialLinks from '~/components/About/SocialLinks.vue'
import PageSEO from '~/components/Shared/PageSEO.vue'

const socialLinks = ref([
  {
    name: 'GitHub',
    url: 'https://github.com/vedmant',
    icon: 'github',
    bgColor: 'bg-gray-800',
    subtitle: '@vedmant',
  },
  {
    name: 'Stack Overflow',
    url: 'https://stackoverflow.com/users/1753349/vedmant',
    icon: 'help-circle',
    bgColor: 'bg-orange-500',
    subtitle: '2.6k reputation',
  },
  // {
  //   name: 'LinkedIn',
  //   url: 'https://www.linkedin.com/in/anatolii-v-25a86690/',
  //   icon: 'linkedin',
  //   bgColor: 'bg-blue-600',
  //   subtitle: 'Professional Network',
  // },
])

const frontendCards = ref([
  {
    title: 'Core Stack',
    emoji: '🛠',
    content: 'Vue.js, Nuxt, Tailwind CSS',
  },
  {
    title: 'Additional Experience',
    emoji: '🔄',
    content: 'React, Svelte, jQuery, Bootstrap, Foundation',
  },
  {
    title: 'UI/UX Focus',
    emoji: '🖥',
    content: 'Mobile-first design, reusable components, cross-browser support',
  },
  {
    title: 'Testing',
    emoji: '⚙️',
    content: 'Jest, Mocha, Cypress (E2E)',
  },
])

const backendCards = ref([
  {
    title: 'Core Stack',
    emoji: '🛠',
    content: 'Laravel, Node.js',
  },
  {
    title: 'API Development',
    emoji: '🔗',
    content: 'RESTful APIs, Laravel Sanctum & Passport, auth systems',
  },
  {
    title: 'Databases',
    emoji: '🗃',
    content: 'MySQL, PostgreSQL, MongoDB, DynamoDB',
  },
  {
    title: 'DevOps',
    emoji: '🌐',
    content: 'Docker, AWS, DigitalOcean, CI/CD pipelines',
  },
])

const infoCards = ref([
  {
    title: 'Work Style',
    emoji: '💼',
    items: [
      'Small remote teams',
      'Collaborate with UI/UX designers',
      'Clear communication via modern tools',
      'Long-term project focus',
    ],
  },
  {
    title: 'Key Strengths',
    emoji: '💡',
    items: [
      'Full stack problem-solving',
      'Clean, maintainable code',
      'Reliable delivery',
      'End-to-end support',
    ],
  },
])
</script>
